import {
  createPdfDetailRows,
  createPdfResult,
  createPdfSection,
  getActiveUnutilisedRows,
  getPdfDeductionAmount,
  getPdfRowAmountByKey,
  getPdfRowName,
  getRoundedPdfAdditionAmount,
  getRoundedPdfDeductionAmount,
  getRoundedPdfRowAmountByKey,
  getSignedUnutilisedLossAmount,
  getSignedPdfAdjustmentAmount,
  hasPdfSectionActivity,
  sumPdfRows,
} from "../shared";
import { isAutoload } from "../../../../utils/hooks/dataHandler";
import { IRAS_MAIN_COMP_LINE_BY_ID } from "./lineConfig";

const getSignedAdjustmentAmount = (row, amountKey = "amount") =>
  getSignedPdfAdjustmentAmount(row, amountKey);

const getOtherSourcedIncomeAmount = (row, amountKey = "amount") =>
  getRoundedPdfRowAmountByKey(row, amountKey);

const isTaxAdjustmentDeduction = (row) => row?.taxAdjustment === "Deduct";

const getUnutilisedYearLabel = (row, prefix = "YA") => {
  if (isAutoload(row?.status)) return getPdfRowName(row);
  if (row?.date)
    return `Financial year ended ${new Date(row.date).getFullYear()}`;
  if (row?.yearOfAssessment) return `${prefix} ${row.yearOfAssessment}`;
  return getPdfRowName(row);
};

const createSection = (id, rows, total, overrides = {}) =>
  createPdfSection(IRAS_MAIN_COMP_LINE_BY_ID, id, rows, total, overrides);

const createResult = (id, amount, overrides = {}) =>
  createPdfResult(IRAS_MAIN_COMP_LINE_BY_ID, id, amount, overrides);

const stripActionPrefix = (label = "") =>
  label.replace(/^(Add\/\(Less\):|Add:|Less:)\s*/i, "");

const createStandaloneSection = (id, rows, total) => {
  const line = IRAS_MAIN_COMP_LINE_BY_ID[id] || {};

  return createSection(id, rows, total, {
    label: stripActionPrefix(line.label),
    suppressSubtotalLine: true,
  });
};

const createCarryForwardCheckpoint = (id, label, amount) =>
  createResult(id, amount, {
    label,
    alwaysRender: true,
    doubleLine: true,
  });

export const buildIrasMainCompSections = ({
  amountKey = "amount",
  netProfitBeforeTax,
  referenceData = {},
  unutilisedCapitalAllowances = [],
  unutilisedDonations = [],
  unutilisedLosses = [],
}) => {
  const taxAdjustmentRows = referenceData.taxAdjustments || [];
  const separateSourceRows =
    referenceData.taxAdjustmentSeparateSourcedIncome || [];
  const balancingChargeRows = referenceData.taxAdjustmentBalancingCharge || [];
  const furtherDeductionS14nRows = referenceData.furtherDeductionS14n || [];
  const furtherDeductionOtherRows = referenceData.furtherDeductionOther || [];
  const currentYearCaRows = referenceData.currentYearCapitalAllowances || [];
  const currentYearDonationRows = referenceData.currentYearDonations || [];
  const otherSourceIncomeRows = referenceData.otherSourceIncome || [];

  const nonTaxableIncomeRows = taxAdjustmentRows.filter(
    isTaxAdjustmentDeduction,
  );
  const nonDeductibleExpenseRows = taxAdjustmentRows.filter(
    (row) => !isTaxAdjustmentDeduction(row),
  );

  const sections = [];
  let runningTotal = parseFloat(netProfitBeforeTax) || 0;

  sections.push(createResult("net_profit_before_tax", runningTotal));

  const separateSourceTotal = sumPdfRows(
    separateSourceRows,
    getSignedAdjustmentAmount,
    amountKey,
  );
  sections.push(
    createSection(
      "separate_source_income_expenses",
      createPdfDetailRows(
        separateSourceRows,
        getSignedAdjustmentAmount,
        getPdfRowName,
        amountKey,
      ),
      separateSourceTotal,
    ),
  );
  runningTotal += separateSourceTotal;

  const nonTaxableIncomeTotal = sumPdfRows(
    nonTaxableIncomeRows,
    getRoundedPdfDeductionAmount,
    amountKey,
  );
  sections.push(
    createSection(
      "non_taxable_income",
      createPdfDetailRows(
        nonTaxableIncomeRows,
        getRoundedPdfDeductionAmount,
        getPdfRowName,
        amountKey,
      ),
      nonTaxableIncomeTotal,
    ),
  );
  runningTotal += nonTaxableIncomeTotal;

  const nonDeductibleExpenseTotal = sumPdfRows(
    nonDeductibleExpenseRows,
    getRoundedPdfAdditionAmount,
    amountKey,
  );
  sections.push(
    createSection(
      "non_tax_deductible_expenses",
      createPdfDetailRows(
        nonDeductibleExpenseRows,
        getRoundedPdfAdditionAmount,
        getPdfRowName,
        amountKey,
      ),
      nonDeductibleExpenseTotal,
    ),
  );
  runningTotal += nonDeductibleExpenseTotal;

  sections.push(
    createResult("adjusted_profit_before_other_deductions", runningTotal),
  );

  const section14nTotal = sumPdfRows(
    furtherDeductionS14nRows,
    getSignedAdjustmentAmount,
    amountKey,
  );
  sections.push(
    createSection(
      "section_14n",
      createPdfDetailRows(
        furtherDeductionS14nRows,
        getSignedAdjustmentAmount,
        getPdfRowName,
        amountKey,
      ),
      section14nTotal,
    ),
  );
  runningTotal += section14nTotal;

  const furtherDeductionOtherTotal = sumPdfRows(
    furtherDeductionOtherRows,
    getSignedAdjustmentAmount,
    amountKey,
  );
  sections.push(
    createSection(
      "further_deductions",
      createPdfDetailRows(
        furtherDeductionOtherRows,
        getSignedAdjustmentAmount,
        getPdfRowName,
        amountKey,
      ),
      furtherDeductionOtherTotal,
    ),
  );
  runningTotal += furtherDeductionOtherTotal;

  const balancingChargeTotal = sumPdfRows(
    balancingChargeRows,
    getSignedAdjustmentAmount,
    amountKey,
  );
  sections.push(
    createSection(
      "balancing_charge",
      createPdfDetailRows(
        balancingChargeRows,
        getSignedAdjustmentAmount,
        getPdfRowName,
        amountKey,
      ),
      balancingChargeTotal,
    ),
  );
  runningTotal += balancingChargeTotal;
  const adjustedProfitBeforeCapitalAllowances = runningTotal;
  const isYearLosses = adjustedProfitBeforeCapitalAllowances < 0;

  sections.push(
    createResult(
      "adjusted_profit_before_capital_allowances",
      adjustedProfitBeforeCapitalAllowances,
      {
        ...(isYearLosses
          ? {
              label: "Current Year Losses",
              alwaysRender: true,
              doubleLine: true,
            }
          : {}),
      },
    ),
  );
  if (isYearLosses) runningTotal = 0;

  if (isYearLosses) {
    const otherSourcedIncomeTotal = sumPdfRows(
      otherSourceIncomeRows,
      getOtherSourcedIncomeAmount,
      amountKey,
    );
    const otherSourcedIncomeDetailRows = createPdfDetailRows(
      otherSourceIncomeRows,
      getOtherSourcedIncomeAmount,
      getPdfRowName,
      amountKey,
    );
    if (
      hasPdfSectionActivity(
        otherSourcedIncomeDetailRows,
        otherSourcedIncomeTotal,
      )
    ) {
      sections.push(
        createStandaloneSection(
          "add_other_sourced_income",
          otherSourcedIncomeDetailRows,
          otherSourcedIncomeTotal,
        ),
      );
    }
    runningTotal += otherSourcedIncomeTotal;

    const activeUnutilisedCA = getActiveUnutilisedRows(
      unutilisedCapitalAllowances,
    );
    const unutilisedCaRows = createPdfDetailRows(
      activeUnutilisedCA,
      getPdfDeductionAmount,
      (row) => getUnutilisedYearLabel(row),
      amountKey,
    );
    const unutilisedCaTotal = -sumPdfRows(activeUnutilisedCA, (row) =>
      Math.abs(getPdfRowAmountByKey(row, amountKey)),
    );
    if (hasPdfSectionActivity(unutilisedCaRows, unutilisedCaTotal)) {
      sections.push(
        createStandaloneSection(
          "unutilised_ca_bf",
          unutilisedCaRows,
          unutilisedCaTotal,
        ),
      );
    }
    runningTotal += unutilisedCaTotal;

    const currentYearCaRowsForDisplay = createPdfDetailRows(
      currentYearCaRows,
      getSignedAdjustmentAmount,
      getPdfRowName,
      amountKey,
    );
    const currentYearCaTotal = sumPdfRows(
      currentYearCaRows,
      getSignedAdjustmentAmount,
      amountKey,
    );
    if (
      hasPdfSectionActivity(currentYearCaRowsForDisplay, currentYearCaTotal)
    ) {
      sections.push(
        createStandaloneSection(
          "current_year_ca",
          currentYearCaRowsForDisplay,
          currentYearCaTotal,
        ),
      );
    }
    runningTotal += currentYearCaTotal;

    const hasCapitalAllowanceCarryForward = runningTotal < 0;
    if (
      hasPdfSectionActivity(
        otherSourcedIncomeDetailRows,
        otherSourcedIncomeTotal,
      ) ||
      hasPdfSectionActivity(unutilisedCaRows, unutilisedCaTotal) ||
      hasPdfSectionActivity(currentYearCaRowsForDisplay, currentYearCaTotal)
    ) {
      sections.push(
        hasCapitalAllowanceCarryForward
          ? createCarryForwardCheckpoint(
              "total_after_capital_allowances",
              "Unutilised CA c/f",
              runningTotal,
            )
          : createResult("total_after_capital_allowances", runningTotal, {
              label: "",
              alwaysRender: true,
            }),
      );
    }
    if (hasCapitalAllowanceCarryForward) runningTotal = 0;

    const activeUnutilisedLosses = getActiveUnutilisedRows(
      unutilisedLosses,
    ).filter((row) => row.reference !== "[Auto Generated] Current Year Losses");
    const unutilisedLossRows = [
      ...createPdfDetailRows(
        activeUnutilisedLosses,
        getSignedUnutilisedLossAmount,
        (row) => getUnutilisedYearLabel(row, "FYE"),
        amountKey,
      ),
      {
        amount: adjustedProfitBeforeCapitalAllowances,
        label: "Current Year Losses",
        sourceResultId: "adjusted_profit_before_capital_allowances",
      },
    ];
    const unutilisedLossesTotal = unutilisedLossRows.reduce(
      (sum, row) => sum + (parseFloat(row.amount) || 0),
      0,
    );
    sections.push(
      (hasCapitalAllowanceCarryForward || runningTotal <= 0
        ? createStandaloneSection
        : createSection)(
        "unutilised_losses_bf",
        unutilisedLossRows,
        unutilisedLossesTotal,
      ),
    );

    let hasLossCarryForward = hasCapitalAllowanceCarryForward;
    if (hasCapitalAllowanceCarryForward) {
      sections.push(
        createCarryForwardCheckpoint(
          "total_before_donations",
          "Unutilised Losses c/f",
          unutilisedLossesTotal,
        ),
      );
    } else {
      runningTotal += unutilisedLossesTotal;
      hasLossCarryForward = runningTotal < 0;
      sections.push(
        hasLossCarryForward
          ? createCarryForwardCheckpoint(
              "total_before_donations",
              "Unutilised Losses c/f",
              runningTotal,
            )
          : createResult("total_before_donations", runningTotal),
      );
      if (hasLossCarryForward) runningTotal = 0;
    }

    const activeUnutilisedDonations =
      getActiveUnutilisedRows(unutilisedDonations);
    const unutilisedDonationRows = createPdfDetailRows(
      activeUnutilisedDonations,
      getPdfDeductionAmount,
      (row) => getUnutilisedYearLabel(row, "FYE"),
      amountKey,
    );
    const unutilisedDonationTotal = -sumPdfRows(
      activeUnutilisedDonations,
      (row) => Math.abs(getPdfRowAmountByKey(row, amountKey)),
    );
    if (
      hasPdfSectionActivity(unutilisedDonationRows, unutilisedDonationTotal)
    ) {
      sections.push(
        (hasLossCarryForward ? createStandaloneSection : createSection)(
          "unutilised_donations_bf",
          unutilisedDonationRows,
          unutilisedDonationTotal,
        ),
      );
    }

    const currentDonationRowsForDisplay = createPdfDetailRows(
      currentYearDonationRows,
      getSignedAdjustmentAmount,
      getPdfRowName,
      amountKey,
    );
    const currentDonationTotal = sumPdfRows(
      currentYearDonationRows,
      getSignedAdjustmentAmount,
      amountKey,
    );
    if (
      hasPdfSectionActivity(currentDonationRowsForDisplay, currentDonationTotal)
    ) {
      sections.push(
        (hasLossCarryForward ? createStandaloneSection : createSection)(
          "current_year_donations",
          currentDonationRowsForDisplay,
          currentDonationTotal,
        ),
      );
    }

    if (hasLossCarryForward) {
      const donationGroupTotal = unutilisedDonationTotal + currentDonationTotal;
      if (
        hasPdfSectionActivity(unutilisedDonationRows, unutilisedDonationTotal) ||
        hasPdfSectionActivity(
          currentDonationRowsForDisplay,
          currentDonationTotal,
        )
      ) {
        sections.push(
          createCarryForwardCheckpoint(
            "total_after_donations",
            "Unutilised Donations c/f",
            donationGroupTotal,
          ),
        );
      }

      return sections;
    }

    runningTotal += unutilisedDonationTotal;
    runningTotal += currentDonationTotal;

    if (runningTotal < 0) {
      sections.push(
        createCarryForwardCheckpoint(
          "total_after_donations",
          "Unutilised Donations c/f",
          runningTotal,
        ),
      );
      return sections;
    }

    sections.push(createResult("total_after_donations", runningTotal));

    return sections;
  }

  const otherSourcedIncomeTotal = sumPdfRows(
    otherSourceIncomeRows,
    getOtherSourcedIncomeAmount,
    amountKey,
  );
  const otherSourcedIncomeRows = createPdfDetailRows(
    otherSourceIncomeRows,
    getOtherSourcedIncomeAmount,
    getPdfRowName,
    amountKey,
  );
  const pushOtherSourcedIncomeSection = () => {
    sections.push(
      createSection(
        "add_other_sourced_income",
        otherSourcedIncomeRows,
        otherSourcedIncomeTotal,
      ),
    );
    runningTotal += otherSourcedIncomeTotal;
  };

  const activeUnutilisedCA = getActiveUnutilisedRows(
    unutilisedCapitalAllowances,
  );
  const unutilisedCaRows = createPdfDetailRows(
    activeUnutilisedCA,
    getPdfDeductionAmount,
    (row) => getUnutilisedYearLabel(row),
    amountKey,
  );
  const unutilisedCaTotal = -sumPdfRows(activeUnutilisedCA, (row) =>
    Math.abs(getPdfRowAmountByKey(row, amountKey)),
  );
  sections.push(
    createSection(
      "unutilised_ca_bf",
      unutilisedCaRows,
      unutilisedCaTotal,
    ),
  );
  runningTotal += unutilisedCaTotal;

  const currentYearCaTotal = sumPdfRows(
    currentYearCaRows,
    getSignedAdjustmentAmount,
    amountKey,
  );
  const currentYearCaRowsForDisplay = createPdfDetailRows(
    currentYearCaRows,
    getSignedAdjustmentAmount,
    getPdfRowName,
    amountKey,
  );
  sections.push(
    createSection(
      "current_year_ca",
      currentYearCaRowsForDisplay,
      currentYearCaTotal,
    ),
  );
  runningTotal += currentYearCaTotal;

  const hasCapitalAllowanceActivity =
    hasPdfSectionActivity(unutilisedCaRows, unutilisedCaTotal) ||
    hasPdfSectionActivity(currentYearCaRowsForDisplay, currentYearCaTotal);
  const hasOtherSourcedIncome = hasPdfSectionActivity(
    otherSourcedIncomeRows,
    otherSourcedIncomeTotal,
  );

  if (hasCapitalAllowanceActivity && hasOtherSourcedIncome) {
    sections.push(
      createResult("total_after_capital_allowances", runningTotal, {
        alwaysRender: true,
      }),
    );
  }

  pushOtherSourcedIncomeSection();

  const hasCapitalAllowanceCarryForward = runningTotal < 0;
  const resultAfterCapitalAllowancesId = hasOtherSourcedIncome
    ? "adjusted_profit_before_losses"
    : "total_after_capital_allowances";
  sections.push(
    hasCapitalAllowanceCarryForward
      ? createCarryForwardCheckpoint(
          resultAfterCapitalAllowancesId,
          "Unutilised CA c/f",
          runningTotal,
        )
      : createResult(resultAfterCapitalAllowancesId, runningTotal, {
          alwaysRender:
            hasCapitalAllowanceActivity || hasOtherSourcedIncome,
        }),
  );
  if (hasCapitalAllowanceCarryForward) {
    runningTotal = 0;
  }

  const activeUnutilisedLosses = getActiveUnutilisedRows(
    unutilisedLosses,
  ).filter((row) => row.reference !== "[Auto Generated] Current Year Losses");
  const unutilisedLossRows = [
    ...createPdfDetailRows(
      activeUnutilisedLosses,
      getSignedUnutilisedLossAmount,
      (row) => getUnutilisedYearLabel(row, "FYE"),
      amountKey,
    ),
  ];
  const unutilisedLossesTotal = unutilisedLossRows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0,
  );
  sections.push(
    (hasCapitalAllowanceCarryForward ? createStandaloneSection : createSection)(
      "unutilised_losses_bf",
      unutilisedLossRows,
      unutilisedLossesTotal,
    ),
  );

  if (hasCapitalAllowanceCarryForward) {
    if (hasPdfSectionActivity(unutilisedLossRows, unutilisedLossesTotal)) {
      sections.push(
        createCarryForwardCheckpoint(
          "total_before_donations",
          "Unutilised Losses c/f",
          unutilisedLossesTotal,
        ),
      );
    }
  } else {
    runningTotal += unutilisedLossesTotal;
    const hasLossCarryForward = runningTotal < 0;
    sections.push(
      hasLossCarryForward
        ? createCarryForwardCheckpoint(
            "total_before_donations",
            "Unutilised Losses c/f",
            runningTotal,
          )
        : createResult("total_before_donations", runningTotal),
    );
    if (hasLossCarryForward) {
      runningTotal = 0;
    }
  }

  const hasLossCarryForward =
    hasCapitalAllowanceCarryForward ||
    sections.some(
      (section) =>
        section.id === "total_before_donations" &&
        section.label === "Unutilised Losses c/f",
    );

  const activeUnutilisedDonations =
    getActiveUnutilisedRows(unutilisedDonations);
  const unutilisedDonationTotal = -sumPdfRows(
    activeUnutilisedDonations,
    (row) => Math.abs(getPdfRowAmountByKey(row, amountKey)),
  );
  const unutilisedDonationRows = createPdfDetailRows(
    activeUnutilisedDonations,
    getPdfDeductionAmount,
    (row) => getUnutilisedYearLabel(row, "FYE"),
    amountKey,
  );
  sections.push(
    (hasLossCarryForward ? createStandaloneSection : createSection)(
      "unutilised_donations_bf",
      unutilisedDonationRows,
      unutilisedDonationTotal,
    ),
  );

  const currentDonationTotal = sumPdfRows(
    currentYearDonationRows,
    getSignedAdjustmentAmount,
    amountKey,
  );
  const currentDonationRowsForDisplay = createPdfDetailRows(
    currentYearDonationRows,
    getSignedAdjustmentAmount,
    getPdfRowName,
    amountKey,
  );
  sections.push(
    (hasLossCarryForward ? createStandaloneSection : createSection)(
      "current_year_donations",
      currentDonationRowsForDisplay,
      currentDonationTotal,
    ),
  );

  if (hasLossCarryForward) {
    const donationGroupTotal = unutilisedDonationTotal + currentDonationTotal;
    if (
      hasPdfSectionActivity(unutilisedDonationRows, unutilisedDonationTotal) ||
      hasPdfSectionActivity(currentDonationRowsForDisplay, currentDonationTotal)
    ) {
      sections.push(
        createCarryForwardCheckpoint(
          "total_after_donations",
          "Unutilised Donations c/f",
          donationGroupTotal,
        ),
      );
    }

    return sections;
  }

  runningTotal += unutilisedDonationTotal;
  runningTotal += currentDonationTotal;

  if (runningTotal < 0) {
    sections.push(
      createCarryForwardCheckpoint(
        "total_after_donations",
        "Unutilised Donations c/f",
        runningTotal,
      ),
    );
    return sections;
  }

  const chargeableIncomeBeforeExemptAmount = runningTotal;
  if (chargeableIncomeBeforeExemptAmount >= 0) {
    sections.push(
      createResult("total_after_donations", chargeableIncomeBeforeExemptAmount),
    );
  }

  return sections;
};
